import { Organization, OrganizationType } from '@cast/types';

type OrganizationGroup = {
  label: string;
  items: Organization[];
  isEscalation: boolean;
};

type GroupedOrganizations = {
  groups: OrganizationGroup[];
  childCounts: Map<string, number>;
};

type CategorizedOrganizations = {
  enterprises: Organization[];
  escalationEnterprises: Organization[];
  internalEnterprises: Organization[];
  defaults: Organization[];
  escalationDefaults: Organization[];
  internalDefaults: Organization[];
};

const groupLabels = {
  myOrgs: 'My organizations',
  customer: 'Customer organizations',
  internal: 'Internal organizations',
} as const;

export const getOrganizationGroup = (org: Organization): string => {
  if (org.organizationMember === false) {
    if (org.internal) {
      return groupLabels.internal;
    }

    return groupLabels.customer;
  }

  return groupLabels.myOrgs;
};

export const buildOrganizationGroups = ({
  enterprises,
  escalationEnterprises,
  internalEnterprises,
  defaults,
  escalationDefaults,
  internalDefaults,
}: CategorizedOrganizations): OrganizationGroup[] => {
  const groups: OrganizationGroup[] = [];
  const myOrgs = [...enterprises, ...defaults];
  const customerOrgs = [...escalationEnterprises, ...escalationDefaults];
  const internalOrgs = [...internalEnterprises, ...internalDefaults];

  if (myOrgs.length > 0) {
    groups.push({
      label: groupLabels.myOrgs,
      items: myOrgs,
      isEscalation: false,
    });
  }

  if (customerOrgs.length > 0) {
    groups.push({
      label: groupLabels.customer,
      items: customerOrgs,
      isEscalation: true,
    });
  }

  if (internalOrgs.length > 0) {
    groups.push({
      label: groupLabels.internal,
      items: internalOrgs,
      isEscalation: true,
    });
  }

  return groups;
};

export const groupOrganizations = (
  organizations: Organization[]
): GroupedOrganizations => {
  const enterprises: Organization[] = [];
  const escalationEnterprises: Organization[] = [];
  const internalEnterprises: Organization[] = [];
  const defaults: Organization[] = [];
  const escalationDefaults: Organization[] = [];
  const internalDefaults: Organization[] = [];
  const childCounts = new Map<string, number>();

  for (const org of organizations) {
    switch (org.type) {
      case OrganizationType.ENTERPRISE:
        if (org.organizationMember === false && org.internal) {
          internalEnterprises.push(org);
        } else if (org.organizationMember === false) {
          escalationEnterprises.push(org);
        } else {
          enterprises.push(org);
        }
        break;
      case OrganizationType.DEFAULT:
        if (org.organizationMember === false && org.internal) {
          internalDefaults.push(org);
        } else if (org.organizationMember === false) {
          escalationDefaults.push(org);
        } else {
          defaults.push(org);
        }
        break;
      case OrganizationType.CHILD:
        if (org.parentId) {
          childCounts.set(
            org.parentId,
            (childCounts.get(org.parentId) || 0) + 1
          );
        }
        break;
    }
  }

  const groups = buildOrganizationGroups({
    enterprises,
    escalationEnterprises,
    internalEnterprises,
    defaults,
    escalationDefaults,
    internalDefaults,
  });

  return { groups, childCounts };
};
